import React, { useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DOCS_PATH, JOBS_PORTAL_PATH } from '../../config/paths';
import LandingFooter from '../../components/landing/LandingFooter';
import LandingHeader from '../../components/landing/LandingHeader';
import {
    HowItWorksBlock,
    JobsWorkspaceBlock,
    ProtocolVisualBlock,
    TestnetOnboardingBlock,
    TrustMetricsBlock,
    TrustPillarsBlock,
    TrustStripBlock,
    UsdcFlowBlock,
    UseCasesBlock,
} from '../../components/landing/LandingSections';
import { chainName, isTestnetMode } from '../../constant/constant';
import {
    motion,
    useMotionValue,
    useScroll,
    useSpring,
    useTransform,
} from 'motion/react';

/* ------------------------------------------------------------------ */
/* Shared motion presets                                              */
/* ------------------------------------------------------------------ */

const fadeUp = {
    hidden: { opacity: 0, y: 28 },
    visible: { opacity: 1, y: 0 },
};

const containerStagger = {
    hidden: {},
    visible: {
        transition: { staggerChildren: 0.08, delayChildren: 0.05 },
    },
};

const inViewProps = {
    initial: 'hidden',
    whileInView: 'visible',
    viewport: { once: true, amount: 0.25 },
};

/* ------------------------------------------------------------------ */
/* Background: animated mouse-tracked spotlight                       */
/* ------------------------------------------------------------------ */

const HeroBackground = () => {
    const x = useMotionValue(0);
    const y = useMotionValue(0);
    const xs = useSpring(x, { stiffness: 80, damping: 20, mass: 0.6 });
    const ys = useSpring(y, { stiffness: 80, damping: 20, mass: 0.6 });

    useEffect(() => {
        const onMove = (e) => {
            x.set(e.clientX - 300);
            y.set(e.clientY - 300);
        };
        window.addEventListener('pointermove', onMove);
        return () => window.removeEventListener('pointermove', onMove);
    }, [x, y]);

    return (
        <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden opacity-30 dark:opacity-100">
            <div className="landing-grid absolute inset-0 opacity-[0.04]" />
            <div className="landing-orb landing-orb-1" />
            <div className="landing-orb landing-orb-2" />
            <div className="landing-orb landing-orb-3" />
            <motion.div
                aria-hidden
                className="absolute h-[600px] w-[600px] rounded-full opacity-[0.08]"
                style={{
                    x: xs,
                    y: ys,
                    background:
                        'radial-gradient(circle, rgba(13,148,136,0.35), transparent 70%)',
                }}
            />
            <div className="absolute inset-x-0 top-0 h-[60vh] bg-[radial-gradient(ellipse_at_top,rgba(13,148,136,0.06),transparent_60%)]" />
        </div>
    );
};

/* ------------------------------------------------------------------ */
/* Floating product preview card (mock dashboard)                     */
/* ------------------------------------------------------------------ */

const HeroPreview = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ['start end', 'end start'],
    });
    const y = useTransform(scrollYProgress, [0, 1], [40, -60]);
    const rotate = useTransform(scrollYProgress, [0, 1], [-2, 2]);

    const txns = [
        {
            type: 'in',
            label: 'Direct payment',
            amount: '+ 1,250 USDC',
            from: '0x9c…4af',
            tone: 'emerald',
        },
        {
            type: 'out',
            label: 'Task escrow funded',
            amount: '- 480 USDC',
            from: '0x32…b1d',
            tone: 'teal',
        },
        {
            type: 'in',
            label: 'Work approved',
            amount: '+ 480 USDC',
            from: 'Task #27',
            tone: 'emerald',
        },
        {
            type: 'in',
            label: 'Payment request paid',
            amount: '+ 2,100 USDC',
            from: '0xab…7f1',
            tone: 'emerald',
        },
    ];

    return (
        <motion.div
            ref={ref}
            initial={{ opacity: 0, y: 60, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
            style={{ y, rotate }}
            className="pointer-events-none relative mx-auto mt-10 w-full max-w-3xl sm:mt-16"
        >
            <div className="pointer-events-none absolute -inset-x-6 -bottom-8 rounded-[40px] bg-[radial-gradient(ellipse_at_center,rgba(13,148,136,0.12),transparent_70%)] blur-2xl sm:-inset-x-10 sm:-bottom-10 dark:bg-[radial-gradient(ellipse_at_center,rgba(13,148,136,0.14),transparent_70%)]" />
            <div className="relative rounded-3xl border border-zinc-200 bg-gradient-to-b from-white to-zinc-100 p-1.5 shadow-xl dark:border-zinc-800/80 dark:from-zinc-900 dark:to-zinc-950 dark:shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)]">
                <div className="rounded-[22px] border border-zinc-200 bg-white dark:border-zinc-800/60 dark:bg-zinc-950">
                    <div className="flex items-center gap-1.5 border-b border-zinc-200 px-4 py-2.5 dark:border-slate-800/60">
                        <span className="h-2.5 w-2.5 rounded-full bg-rose-400/70" />
                        <span className="h-2.5 w-2.5 rounded-full bg-amber-400/70" />
                        <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/70" />
                        <span className="ml-3 text-[11px] font-medium text-zinc-500 dark:text-zinc-500">
                            cronpay.in / dashboard
                        </span>
                    </div>
                    <div className="grid gap-4 p-5 sm:grid-cols-3">
                        {[
                            { label: 'USDC received', val: '24,580', tone: 'emerald' },
                            { label: 'USDC sent', val: '8,910', tone: 'teal' },
                            { label: 'In escrow', val: '3,420', tone: 'amber' },
                        ].map((card) => (
                            <div
                                key={card.label}
                                className="rounded-xl border border-zinc-200 bg-zinc-50 p-4 dark:border-zinc-800/70 dark:bg-zinc-900/60"
                            >
                                <p className="text-[11px] uppercase tracking-wider text-zinc-500 dark:text-slate-500">
                                    {card.label}
                                </p>
                                <p
                                    className={`mt-1 text-lg font-semibold ${card.tone === 'emerald'
                                        ? 'text-emerald-700 dark:text-emerald-300'
                                        : card.tone === 'teal'
                                            ? 'text-teal-700 dark:text-teal-300'
                                            : 'text-amber-700 dark:text-amber-300'
                                        }`}
                                >
                                    {card.val}{' '}
                                    <span className="text-sm font-medium text-zinc-500 dark:text-zinc-500">
                                        USDC
                                    </span>
                                </p>
                            </div>
                        ))}
                    </div>
                    <div className="border-t border-zinc-200 px-5 pb-5 dark:border-slate-800/60">
                        <p className="mb-2 mt-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500 dark:text-slate-500">
                            Recent activity
                        </p>
                        <ul className="divide-y divide-zinc-200 dark:divide-slate-800/60">
                            {txns.map((t, i) => (
                                <motion.li
                                    key={i}
                                    initial={{ opacity: 0, x: -8 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{
                                        duration: 0.5,
                                        delay: 1.1 + i * 0.12,
                                    }}
                                    className="flex items-center gap-3 py-3"
                                >
                                    <span
                                        className={`inline-flex h-8 w-8 items-center justify-center rounded-lg border ${t.tone === 'emerald'
                                            ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300'
                                            : 'border-teal-200 bg-teal-50 text-teal-700 dark:border-teal-500/30 dark:bg-teal-500/10 dark:text-teal-300'
                                            }`}
                                    >
                                        <i
                                            className={`fa-solid ${t.type === 'in'
                                                ? 'fa-arrow-down rotate-45'
                                                : 'fa-arrow-up rotate-45'
                                                } text-xs`}
                                        />
                                    </span>
                                    <div className="min-w-0 flex-1">
                                        <p className="truncate text-sm text-zinc-800 dark:text-slate-200">
                                            {t.label}
                                        </p>
                                        <p className="truncate text-[11px] text-zinc-500 dark:text-slate-500">
                                            {t.from}
                                        </p>
                                    </div>
                                    <p
                                        className={`text-sm font-semibold ${t.tone === 'emerald'
                                            ? 'text-emerald-700 dark:text-emerald-300'
                                            : 'text-teal-700 dark:text-teal-300'
                                            }`}
                                    >
                                        {t.amount}
                                    </p>
                                </motion.li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

/* ------------------------------------------------------------------ */
/* Feature card with motion hover                                     */
/* ------------------------------------------------------------------ */

const FeatureCard = ({ icon, title, description, gradient, accent }) => (
    <motion.div
        variants={fadeUp}
        whileHover={{ y: -6 }}
        transition={{ type: 'spring', stiffness: 220, damping: 22 }}
        className="group relative overflow-hidden rounded-2xl border border-zinc-200 bg-white p-7 shadow-sm dark:border-zinc-800 dark:bg-zinc-900/80 sm:p-8"
    >
        <div
            className={`pointer-events-none absolute -top-24 -right-24 h-48 w-48 rounded-full opacity-0 blur-3xl transition-opacity duration-500 group-hover:opacity-30 ${gradient}`}
        />
        <div
            className={`relative inline-flex h-14 w-14 items-center justify-center rounded-xl border ${accent.border} ${accent.bg} mb-5`}
        >
            <i className={`${icon} text-xl ${accent.text}`} />
        </div>
        <h3 className="relative mb-2.5 text-lg font-semibold text-zinc-900 dark:text-slate-100">
            {title}
        </h3>
        <p className="relative text-sm leading-relaxed text-zinc-600 dark:text-slate-400">
            {description}
        </p>
        <div
            className={`absolute bottom-0 left-0 right-0 h-[2px] origin-left scale-x-0 bg-gradient-to-r ${gradient} transition-transform duration-500 group-hover:scale-x-100`}
        />
    </motion.div>
);

/* ------------------------------------------------------------------ */
/* Jobs & company workspace                                           */
/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */
/* Main landing page                                                  */
/* ------------------------------------------------------------------ */

const Login = () => {
    const navigate = useNavigate();

    const handleLaunch = () => {
        localStorage.setItem('userData', JSON.stringify({ landing: true }));
        navigate('/dashboard');
    };

    const handleOpenJobsPortal = () => {
        localStorage.setItem('userData', JSON.stringify({ landing: true }));
        navigate(JOBS_PORTAL_PATH);
    };

    const scrollToSection = (id) => {
        document.getElementById(id)?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        });
    };

    const features = [
        {
            icon: 'fa-solid fa-bolt',
            title: 'Direct pay',
            description: 'Send and receive USDC to any wallet in one step.',
            gradient: 'from-teal-600 to-teal-500',
            accent: {
                border: 'border-teal-500/30',
                bg: 'bg-teal-500/10',
                text: 'text-teal-600 dark:text-teal-300',
            },
        },
        {
            icon: 'fa-solid fa-lock',
            title: 'Tracked escrow',
            description: 'Lock USDC on-chain and release when milestones are met.',
            gradient: 'from-emerald-600 to-teal-600',
            accent: {
                border: 'border-emerald-500/30',
                bg: 'bg-emerald-500/10',
                text: 'text-emerald-600 dark:text-emerald-300',
            },
        },
        {
            icon: 'fa-solid fa-briefcase',
            title: 'Company & Job',
            description: 'Tasks, team payouts, and USDC escrow in one workspace.',
            gradient: 'from-teal-700 to-emerald-600',
            accent: {
                border: 'border-teal-500/30',
                bg: 'bg-teal-500/10',
                text: 'text-teal-600 dark:text-teal-300',
            },
        },
    ];

    return (
        <div className="crypto-page relative min-h-screen bg-zinc-50 text-zinc-900 dark:bg-[#09090b] dark:text-zinc-100">
            <HeroBackground />
            <LandingHeader
                onLaunch={handleLaunch}
                onOpenJobs={handleOpenJobsPortal}
            />

            <div className="relative z-10 overflow-x-hidden">
                {/* ============== HERO ============== */}
                <section className="relative pb-16 pt-8 sm:pb-28 sm:pt-12 lg:pt-16">
                    <div className="pointer-events-none absolute left-1/2 top-1/3 h-[280px] w-[min(100%,480px)] -translate-x-1/2 -translate-y-1/2 rounded-full bg-teal-500/10 blur-[100px] dark:bg-teal-600/10 sm:h-[400px] sm:w-[600px] sm:blur-[120px]" />

                    <motion.div
                        variants={containerStagger}
                        initial="hidden"
                        animate="visible"
                        className="relative z-20 mx-auto max-w-6xl px-4 sm:px-6 lg:px-8"
                    >
                        <div className="relative z-20 mx-auto max-w-3xl text-center">
                            <motion.div
                                variants={fadeUp}
                                className="flex flex-wrap items-center justify-center gap-2"
                            >
                                <span className="inline-flex items-center gap-1.5 rounded-full border border-teal-200 bg-teal-50 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-teal-800 dark:border-teal-800/60 dark:bg-teal-950/50 dark:text-teal-300">
                                    <span className="relative flex h-1.5 w-1.5">
                                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-teal-500 opacity-75" />
                                        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-teal-600 dark:bg-teal-400" />
                                    </span>
                                    {chainName || 'Arc'}
                                </span>
                                <span className="rounded-full border border-zinc-200 bg-white px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-zinc-700 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300">
                                    USDC-native
                                </span>
                                <span className="rounded-full border border-zinc-200 bg-white px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-zinc-700 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300">
                                    Non-custodial
                                </span>
                            </motion.div>

                            <motion.h1
                                variants={fadeUp}
                                className="mt-6 text-4xl font-bold leading-[1.05] tracking-tight text-zinc-900 dark:text-white sm:text-5xl lg:text-7xl"
                            >
                                Structured payments on Arc.{' '}
                                <span className="landing-gradient-text">
                                    Built for trust.
                                </span>
                            </motion.h1>

                            <motion.p
                                variants={fadeUp}
                                className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-zinc-600 dark:text-slate-400 sm:text-lg"
                            >
                                Send, escrow, and pay teams in USDC from your wallet. Direct pay,
                                tracked milestones, payment requests, and task payouts on-chain.
                            </motion.p>

                            <motion.div
                                variants={fadeUp}
                                className="relative z-20 mx-auto mt-8 flex w-full max-w-md flex-col gap-3 sm:mt-10 sm:max-w-none sm:flex-row sm:flex-wrap sm:items-center sm:justify-center sm:gap-4"
                            >
                                <button
                                    type="button"
                                    onClick={handleLaunch}
                                    className="touch-manipulation group relative inline-flex min-h-[48px] w-full items-center justify-center gap-2.5 overflow-hidden rounded-2xl bg-teal-600 px-8 py-3.5 text-base font-semibold text-white shadow-md shadow-teal-900/20 active:scale-[0.98] dark:bg-gradient-to-r dark:from-teal-700 dark:to-teal-600 sm:w-auto sm:min-h-[44px] sm:px-8 sm:py-4"
                                >
                                    <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
                                    <span className="relative z-10 flex items-center gap-2.5">
                                        Launch app
                                        <i className="fa-solid fa-arrow-right text-sm" />
                                    </span>
                                </button>
                                <button
                                    type="button"
                                    onClick={handleOpenJobsPortal}
                                    className="touch-manipulation inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-2xl border border-teal-300 bg-teal-50 px-8 py-3.5 text-base font-semibold text-teal-900 transition-colors hover:bg-teal-100 active:scale-[0.98] dark:border-teal-700 dark:bg-teal-950/40 dark:text-teal-200 dark:hover:bg-teal-950/60 sm:w-auto sm:min-h-[44px] sm:py-4"
                                >
                                    Company &amp; Job
                                    <i className="fa-solid fa-briefcase text-sm" />
                                </button>
                            </motion.div>

                            {isTestnetMode ? (
                                <motion.p
                                    variants={fadeUp}
                                    className="mt-4 text-sm text-zinc-500 dark:text-zinc-500"
                                >
                                    <button
                                        type="button"
                                        onClick={() => scrollToSection('try-testnet')}
                                        className="font-medium text-teal-700 underline-offset-2 hover:underline dark:text-teal-400"
                                    >
                                        New here?
                                    </button>{' '}
                                    Try testnet USDC in under 2 minutes.
                                </motion.p>
                            ) : null}
                        </div>

                        <div className="relative z-0">
                            <HeroPreview />
                        </div>
                    </motion.div>
                </section>

                <TrustStripBlock />

                <UsdcFlowBlock />

                {/* ============== FEATURES ============== */}
                <section
                    id="features"
                    className="scroll-mt-20 mx-auto max-w-6xl px-4 py-14 sm:scroll-mt-24 md:scroll-mt-8 sm:px-6 sm:py-20 lg:px-8"
                >
                    <motion.div
                        variants={containerStagger}
                        {...inViewProps}
                        className="mb-10 text-center sm:mb-12"
                    >
                        <motion.span
                            variants={fadeUp}
                            className="mb-3 inline-block text-[11px] font-semibold uppercase tracking-[0.22em] text-teal-700 dark:text-teal-400"
                        >
                            Product
                        </motion.span>
                        <motion.h2
                            variants={fadeUp}
                            className="text-3xl font-bold tracking-tight text-zinc-900 dark:text-white sm:text-4xl"
                        >
                            Everything you need in USDC
                        </motion.h2>
                    </motion.div>

                    <motion.div
                        variants={containerStagger}
                        {...inViewProps}
                        className="grid gap-6 sm:grid-cols-3 sm:gap-8"
                    >
                        {features.map((item) => (
                            <FeatureCard key={item.title} {...item} />
                        ))}
                    </motion.div>
                </section>

                <UseCasesBlock onLaunch={handleLaunch} onOpenJobs={handleOpenJobsPortal} />

                <ProtocolVisualBlock />

                <TrustPillarsBlock />

                <JobsWorkspaceBlock onOpenJobs={handleOpenJobsPortal} />

                <HowItWorksBlock />

                <TestnetOnboardingBlock onLaunch={handleLaunch} />

                <TrustMetricsBlock />

                {/* ============== BOTTOM CTA ============== */}
                <section className="border-t border-zinc-200 dark:border-slate-800/60">
                    <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 sm:py-20 lg:px-8">
                        <motion.div
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.3 }}
                            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                            className="relative overflow-hidden rounded-3xl border border-zinc-200 bg-white p-8 text-center shadow-sm dark:border-zinc-800 dark:bg-zinc-900/80 sm:p-12"
                        >
                            <div className="pointer-events-none absolute -top-10 left-1/2 h-[300px] w-[500px] -translate-x-1/2 rounded-full bg-teal-500/10 blur-[100px] dark:bg-teal-700/12" />

                            <h2 className="relative mb-3 text-2xl font-bold text-zinc-900 dark:text-white sm:text-3xl">
                                Start paying in USDC on Arc
                            </h2>
                            <p className="relative mx-auto mb-6 max-w-md text-sm text-zinc-600 dark:text-zinc-400">
                                Launch the app, get test USDC if needed, and start paying from
                                the dashboard or team workspace.
                            </p>
                            <div className="relative flex flex-col items-center justify-center gap-3 sm:flex-row">
                                <motion.button
                                    type="button"
                                    onClick={handleLaunch}
                                    whileHover={{ y: -3 }}
                                    whileTap={{ y: -1 }}
                                    transition={{
                                        type: 'spring',
                                        stiffness: 300,
                                        damping: 22,
                                    }}
                                    className="group relative inline-flex items-center justify-center gap-2.5 overflow-hidden rounded-2xl bg-teal-600 px-10 py-4 text-base font-semibold text-white shadow-md shadow-teal-900/20 dark:bg-gradient-to-r dark:from-teal-700 dark:to-teal-600 dark:shadow-[0_12px_40px_-8px_rgba(15,118,110,0.45)]"
                                >
                                    <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
                                    <span className="relative z-10 flex items-center gap-2.5">
                                        Payment dashboard
                                        <i className="fa-solid fa-arrow-right text-sm transition-transform duration-300 group-hover:translate-x-1.5" />
                                    </span>
                                </motion.button>
                                <motion.button
                                    type="button"
                                    onClick={handleOpenJobsPortal}
                                    whileHover={{ y: -3 }}
                                    whileTap={{ y: -1 }}
                                    className="inline-flex items-center gap-2 rounded-2xl border border-teal-300 bg-teal-50 px-8 py-4 text-base font-semibold text-teal-900 hover:bg-teal-100 dark:border-teal-700 dark:bg-teal-950/50 dark:text-teal-200 dark:hover:bg-teal-950/70"
                                >
                                    Company &amp; Job
                                    <i className="fa-solid fa-briefcase text-sm" />
                                </motion.button>
                            </div>
                        </motion.div>
                    </div>
                </section>

                <LandingFooter
                    onLaunch={handleLaunch}
                    onOpenJobs={handleOpenJobsPortal}
                />
            </div>
        </div>
    );
};

export default Login;
